import { useCallback, useEffect, useState } from "react";
import { settingsService } from "@/services";
import type { NumberingKey, NumberingRule } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { canManageNumbering } from "@/config/roles";
import { PageHeader } from "@/components/feature/PageHeader";
import { SettingsNav } from "../components/SettingsNav";
import { Card } from "@/components/base/Card";
import { Field } from "@/components/base/Field";
import { Toggle } from "@/components/base/Toggle";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { ErrorState } from "@/components/base/ErrorState";
import { EmptyState } from "@/components/base/EmptyState";

interface RuleDef {
  id: NumberingKey;
  title: string;
  example: string;
}

const RULE_DEFS: RuleDef[] = [
  { id: "member_code", title: "Member codes", example: "MEM0001" },
  { id: "group_code", title: "Group codes", example: "KGT001" },
  { id: "receipt", title: "Receipt numbers", example: "RCPT01041" },
  { id: "payout_voucher", title: "Payout vouchers", example: "PV00127" },
];

function formatPreview(rule: NumberingRule): string {
  return `${rule.prefix}${String(rule.next).padStart(rule.padding, "0")}`;
}

export default function NumberingSettingsPage() {
  const { role, user } = useAuth();
  const toast = useToast();
  const canManage = canManageNumbering(role);

  const [rules, setRules] = useState<NumberingRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<NumberingKey | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const n = await settingsService.getNumbering();
      setRules(n.rules);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load numbering settings.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (id: NumberingKey, field: Partial<NumberingRule>) =>
    setRules((rs) => (rs ? rs.map((r) => (r.id === id ? { ...r, ...field } : r)) : rs));

  const save = async (rule: NumberingRule) => {
    if (!canManage || savingId) return;
    const clampedNext = Math.max(rule.next, rule.next);
    setSavingId(rule.id);
    try {
      await settingsService.updateNumberingRule(rule.id, { prefix: rule.prefix, padding: rule.padding, next: clampedNext, resetAnnually: rule.resetAnnually }, user?.name ?? "Staff");
      toast.success("Numbering updated", `${rule.title}: next is now ${formatPreview(rule)}.`);
      await load();
    } catch (err) {
      toast.error("Could not save", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Organization configuration, staff users and the audit trail." />
      <SettingsNav />

      {!canManage ? (
        <EmptyState
          icon="ri-lock-line"
          title="Super Admin only"
          description="Only a Super Admin can change document numbering. This protects the integrity of financial records."
        />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !rules ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <div className="space-y-4">
          <Alert variant="info" title="Two rules protect your records">
            <div className="space-y-1">
              <p>1. The next number can be moved forward but never backward — numbers already issued are never reused.</p>
              <p>2. Changing a prefix never renumbers anything already issued. It only affects new numbers from now on.</p>
            </div>
          </Alert>

          {RULE_DEFS.map((def) => {
            const rule = rules.find((r) => r.id === def.id);
            if (!rule) return null;
            return (
              <Card
                key={def.id}
                title={def.title}
                subtitle={def.example}
                actions={
                  <Button size="sm" icon={<i className="ri-save-line" />} loading={savingId === def.id} disabled={!canManage} onClick={() => save(rule)}>
                    Save
                  </Button>
                }
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Prefix">
                    <input
                      value={rule.prefix}
                      onChange={(e) => patch(def.id, { prefix: e.target.value })}
                      disabled={!canManage}
                      className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:bg-background-100 disabled:text-foreground-500"
                    />
                  </Field>
                  <Field label="Padding width">
                    <input
                      type="number"
                      min={1}
                      max={6}
                      value={rule.padding}
                      onChange={(e) => patch(def.id, { padding: Math.max(1, Math.min(6, Number(e.target.value) || 1)) })}
                      disabled={!canManage}
                      className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:bg-background-100 disabled:text-foreground-500"
                    />
                  </Field>
                  <Field label="Next number" helper="Moves forward only — never lower than the current value.">
                    <input
                      type="number"
                      min={1}
                      value={rule.next}
                      onChange={(e) => patch(def.id, { next: Math.max(1, Number(e.target.value) || 1) })}
                      disabled={!canManage}
                      className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:bg-background-100 disabled:text-foreground-500"
                    />
                  </Field>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Toggle
                      checked={rule.resetAnnually}
                      onChange={async (v) => {
                        patch(def.id, { resetAnnually: v });
                        await save({ ...rule, resetAnnually: v });
                      }}
                      disabled={!canManage}
                      size="sm"
                      label="Reset annually"
                    />
                    <span className="text-xs text-foreground-500">Many businesses prefer fresh receipt books each year.</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100/60 px-3 py-1.5">
                    <span className="text-xs text-foreground-500">Next format</span>
                    <span className="tnum font-mono text-sm font-semibold text-foreground-900">{formatPreview(rule)}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}